/**
 * @deprecated Spot-only legacy auction-params client, superseded by
 * `@velocity-exchange/common` `fetchAuctionOrderParams` for perp per
 * docs/adr/0001-auction-params-single-client.md. Do not extend for perp
 * flows; spot is deprecated and this module is slated for deletion once
 * spot execution is removed.
 */
import {
	BN,
	MarketType,
	PositionDirection,
	L2OrderBook,
	BigNum,
	ZERO,
	VelocityClient,
	OptionalOrderParams,
	PERCENTAGE_PRECISION_EXP,
	OrderSubscriber,
	SlotDurationMs,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	L2WithOracleAndMarketData,
	TradeOffsetPrice,
	calculateSpreadBidAskMark,
	deriveMarketOrderParams,
	getPriceObject,
	DEFAULT_MARKET_AUCTION_DURATION_MS,
	mapAuctionParamsResponse,
	type MappedAuctionParams,
} from '@velocity-exchange/common';
import Env from '../environmentVariables/EnvironmentVariables';
import captureException from './captureException';
import { fetchBulkMarketL2Data } from '../hooks/dlob/useSyncMarketDataWithDlobServerPolling';
import {
	getPerpMarketL2State,
	getSpotMarketL2State,
} from '../hooks/dlob/useSyncMarketDataWithBlockchain';
import {
	calculatePriceImpactFromL2,
	calculateShowPriceEstimateOracleDivergenceWarning,
} from './priceImpactUtils';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { DEFAULT_MARKET_AUCTION_DURATION_SECONDS } from 'src/environmentVariables/DefaultSettings';
import {
	auctionDurationMsToSlots,
	auctionDurationSecondsToSlots,
} from './auctionDuration';

// Dynamic slippage configuration
const DYNAMIC_SLIPPAGE_MIN = 0.035;
const DYNAMIC_SLIPPAGE_MAX = 5;
const DYNAMIC_BASE_SLIPPAGE_MAJOR = 0;
const DYNAMIC_BASE_SLIPPAGE_MID_MAJOR = 0.25;
const DYNAMIC_BASE_SLIPPAGE_NON_MAJOR = 0.5;
const DYNAMIC_SLIPPAGE_MULTIPLIER_MAJOR = 1.1;
const DYNAMIC_SLIPPAGE_MULTIPLIER_MID_MAJOR = 1.25;
const DYNAMIC_SLIPPAGE_MULTIPLIER_NON_MAJOR = 1.5;
const DYNAMIC_CROSS_SPREAD_CAP = 0.1;
const MID_MAJOR_MARKETS = [13, 23, 4, 59, 9, 76]; // XRP, WIF, BONK, HYPE, SUI, ASTER

// Default auction ramp by API version, as the wall-clock intent each version
// was chosen for. The v3 entry mirrors the dlob server's FAST_FILL_AUCTION_DURATION_MS.
const DEFAULT_AUCTION_DURATION_MS_BY_VERSION: Record<number, number> = {
	1: DEFAULT_MARKET_AUCTION_DURATION_MS,
	2: 4_000,
	3: 2_000,
};

// Server response types
interface OrderParamsServerResponse {
	orderType: string;
	marketType: string;
	userOrderId: number;
	direction: string;
	baseAssetAmount: string;
	marketIndex: number;
	reduceOnly: boolean;
	postOnly: string;
	bitFlags: number;
	triggerPrice: string | null;
	triggerCondition: string;
	oraclePriceOffset: number;
	auctionDuration: number;
	maxTs: string | null;
	auctionStartPrice: string;
	auctionEndPrice: string;
}

interface OrderParamsResponseData {
	params: OrderParamsServerResponse;
	entryPrice: string;
	markPrice: string;
	bestPrice: string;
	worstPrice: string;
	oraclePrice: string;
	priceImpact: number;
	slippageTolerance: number;
	baseAvailable?: string;
	exceedsLiquidity?: boolean;
}

interface OrderParamsResponseBody {
	data: OrderParamsResponseData;
}

// This is what callers expect to receive - fully processed and ready to use
export interface ProcessedOrderParamsResponse {
	// Mapped auction parameters
	auctionParams: MappedAuctionParams;

	// Processed slippage (always a number)
	slippage: number;

	// Processed price impact data
	priceImpact: {
		entryPrice: BN;
		markPrice: BN;
		priceImpact: BN;
		baseAvailable: BN;
		bestPrice: BN;
		worstPrice: BN;
		exceedsLiquidity: boolean;
		showPriceEstimateOracleDivergenceWarning: boolean;
	};

	oraclePrice: BN;

	source: 'endpoint' | 'server' | 'blockchain';
}

/**
 * Calculate dynamic slippage tolerance using L2 data.
 * Normally the params endpoint will take care of this calculation.
 * Only in the case of a fallback where we poll for the L2, this function will calculate dynamic slippage.
 */
const calculateDynamicSlippageFromL2 = (
	l2Data: L2OrderBook,
	marketId: MarketId,
	startPrice: BN,
	worstPrice: BN,
	oraclePrice?: BN,
	apiVersion?: number
): number => {
	const isMajor = marketId.isPerp && marketId.marketIndex < 3;
	const isMidMajor =
		marketId.isPerp && MID_MAJOR_MARKETS.includes(marketId.marketIndex);

	const baseSlippage = isMajor
		? DYNAMIC_BASE_SLIPPAGE_MAJOR
		: isMidMajor
			? DYNAMIC_BASE_SLIPPAGE_MID_MAJOR
			: DYNAMIC_BASE_SLIPPAGE_NON_MAJOR;

	// Calculate spread using L2 data
	let spreadBaseSlippage = 0.0005; // 0.05% fallback spread
	try {
		const spreadInfo = calculateSpreadBidAskMark(l2Data, oraclePrice);

		const spreadPctNum = BigNum.from(
			spreadInfo.spreadPct,
			PERCENTAGE_PRECISION_EXP
		)?.toNum();

		if (spreadInfo?.spreadPct && Number.isFinite(spreadPctNum)) {
			spreadBaseSlippage = spreadPctNum * 0.9;

			// If the L2 is crossed (best bid > best ask), cap the spread contribution
			const bestBid = spreadInfo.bestBidPrice;
			const bestAsk = spreadInfo.bestAskPrice;
			const isCrossed = !!(bestBid && bestAsk && bestBid.gt(bestAsk));

			if (isCrossed) {
				spreadBaseSlippage = Math.min(
					spreadBaseSlippage,
					DYNAMIC_CROSS_SPREAD_CAP
				);
			}
		}
	} catch (error) {
		console.warn('Failed to calculate spread, using fallback:', error);
	}

	let dynamicSlippage = baseSlippage + spreadBaseSlippage;

	// use halfway to worst price as size adjusted slippage
	if (startPrice && worstPrice) {
		const sizeAdjustedSlippage =
			(startPrice.sub(worstPrice).abs().toNumber() /
				startPrice.toNumber() /
				2) *
			100;

		dynamicSlippage = Math.max(dynamicSlippage, sizeAdjustedSlippage);
	}

	// Apply multiplier based on market tier
	const multiplier = isMajor
		? DYNAMIC_SLIPPAGE_MULTIPLIER_MAJOR
		: isMidMajor
			? DYNAMIC_SLIPPAGE_MULTIPLIER_MID_MAJOR
			: DYNAMIC_SLIPPAGE_MULTIPLIER_NON_MAJOR;

	dynamicSlippage = dynamicSlippage * multiplier;

	// Enforce minimum/maximum slippage bounds
	let finalSlippage = Math.min(
		Math.max(dynamicSlippage, DYNAMIC_SLIPPAGE_MIN),
		DYNAMIC_SLIPPAGE_MAX
	);

	// Apply 10% boost for API v2 and v3
	if (apiVersion === 2 || apiVersion === 3) {
		finalSlippage = finalSlippage * 1.1;
	}

	// Round to avoid floating point precision issues, preserving 6 decimal places
	return Math.round(finalSlippage * 1000000) / 1000000;
};

// L2 fetching function using the actual implementation from useSyncMarketDataWithDlobServerPolling
const fetchL2DataForMarket = async (
	marketId: MarketId
): Promise<L2WithOracleAndMarketData[]> => {
	try {
		const l2Data = await fetchBulkMarketL2Data([
			{
				marketId,
				depth: 100, // Default depth for fallback
				intervalMultiplier: 1, // Use live multiplier for fallback
			},
		]);
		return l2Data;
	} catch (error) {
		console.warn('Failed to fetch L2 data for fallback:', error);
		throw error;
	}
};

// Helper function to convert L2WithOracleAndMarketData to L2OrderBook
const convertToL2OrderBook = (
	l2Data: L2WithOracleAndMarketData[]
): L2OrderBook => {
	// Find the market data - there should be one entry for our market
	const marketData = l2Data[0];

	if (!marketData) {
		throw new Error('No L2 data available for market');
	}

	// L2WithOracleAndMarketData extends L2OrderBook, so we can use it directly
	return {
		asks: marketData.asks,
		bids: marketData.bids,
		slot: marketData.slot,
	};
};

// Helper function to convert OptionalOrderParams to MappedAuctionParams
const convertOptionalOrderParamsToMappedAuctionParams = (
	orderParams: OptionalOrderParams
): MappedAuctionParams => {
	return {
		orderType: orderParams.orderType,
		marketType: orderParams.marketType ?? MarketType.PERP,
		userOrderId: orderParams.userOrderId,
		direction: orderParams.direction,
		baseAssetAmount: orderParams.baseAssetAmount,
		marketIndex: orderParams.marketIndex,
		reduceOnly: orderParams.reduceOnly,
		postOnly: orderParams.postOnly,
		//immediateOrCancel: orderParams.immediateOrCancel,
		triggerPrice: orderParams.triggerPrice,
		triggerCondition: orderParams.triggerCondition,
		oraclePriceOffset: orderParams.oraclePriceOffset
			? new BN(orderParams.oraclePriceOffset.toString())
			: undefined,
		auctionDuration: orderParams.auctionDuration ?? undefined,
		maxTs: orderParams.maxTs ?? undefined,
		auctionStartPrice: orderParams.auctionStartPrice ?? undefined,
		auctionEndPrice: orderParams.auctionEndPrice ?? undefined,
	};
};

const getOrderParamsWithBlockchainFallback = async (params: {
	marketId: MarketId;
	direction: PositionDirection;
	baseAmount: BN;
	reduceOnly: boolean;
	auctionDuration: number;
	auctionStartPriceOffset: number;
	auctionEndPriceOffset: number;
	auctionStartPriceOffsetFrom: TradeOffsetPrice;
	auctionEndPriceOffsetFrom: TradeOffsetPrice;
	auctionPriceCaps?: { min: BN; max: BN };
	slippageTolerance: number | 'dynamic';
	isOracleOrder?: boolean;
	additionalEndPriceBuffer?: BN;
	forceUpToSlippage?: boolean;
	maxLeverageSelected: boolean;
	maxLeverageOrderSize: BN;
	velocityClient: VelocityClient;
}): Promise<ProcessedOrderParamsResponse> => {
	if (!params.velocityClient) {
		throw new Error(
			'Blockchain fallback requires driftClient and getMarketPriceData'
		);
	}

	const oraclePriceData = params.marketId.isPerp
		? params.velocityClient.getOracleDataForPerpMarket(
				params.marketId.marketIndex
			)
		: params.velocityClient.getOracleDataForSpotMarket(
				params.marketId.marketIndex
			);

	// Create OrderSubscriber for DLOB data
	const orderSubscriber = new OrderSubscriber({
		velocityClient: params.velocityClient,
		subscriptionConfig: {
			type: 'polling',
			frequency: 1000,
		},
	});

	try {
		// Subscribe to get data
		orderSubscriber.subscribe();

		// Get current slot and DLOB
		// Use oracle slot fallback in this non-hook context.
		const currentSlot = oraclePriceData?.slot?.toNumber() ?? 0;
		const newDlob = await orderSubscriber.getDLOB(currentSlot);

		// Get blockchain-based L2 data using the centralized functions
		let l2Data: L2OrderBook;
		if (ENUM_UTILS.match(params.marketId.marketType, MarketType.PERP)) {
			const perpResults = getPerpMarketL2State(
				[params.marketId],
				newDlob,
				currentSlot,
				params.velocityClient
			);
			l2Data = perpResults[0];
		} else {
			const spotResults = getSpotMarketL2State(
				[params.marketId],
				newDlob,
				currentSlot,
				params.velocityClient
			);
			l2Data = spotResults[0];
		}

		// Get oracle and mark prices
		const oraclePrice = oraclePriceData?.price;
		const markPrice = calculateSpreadBidAskMark(l2Data, oraclePrice)?.markPrice;

		if (!oraclePrice || !markPrice) {
			throw new Error('Missing oracle or mark price for blockchain fallback');
		}

		// Calculate price impact
		const priceImpactData = calculatePriceImpactFromL2(
			params.marketId,
			params.direction,
			params.baseAmount,
			l2Data,
			oraclePrice
		);

		const derivedSlippage =
			params.slippageTolerance === 'dynamic'
				? calculateDynamicSlippageFromL2(
						l2Data,
						params.marketId,
						priceImpactData.bestPrice, // Use best price as start price
						priceImpactData.worstPrice,
						oraclePrice
					)
				: typeof params.slippageTolerance === 'number'
					? params.slippageTolerance
					: 0.005;

		// Use deriveMarketOrderParams to get auction params
		const auctionParams = deriveMarketOrderParams({
			marketType: params.marketId.marketType,
			marketIndex: params.marketId.marketIndex,
			direction: params.direction,
			baseAmount: params.baseAmount,
			reduceOnly: params.reduceOnly,
			allowInfSlippage: false,
			worstPrice: priceImpactData.worstPrice,
			auctionDuration: params.auctionDuration,
			auctionStartPriceOffset: params.auctionStartPriceOffset,
			auctionEndPriceOffset: params.auctionEndPriceOffset,
			maxLeverageSelected: params.maxLeverageSelected,
			maxLeverageOrderSize: params.maxLeverageOrderSize,
			oraclePrice: oraclePrice,
			markPrice: markPrice,
			bestPrice: priceImpactData.bestPrice,
			entryPrice: priceImpactData.entryPrice,
			auctionStartPriceOffsetFrom: params.auctionStartPriceOffsetFrom,
			auctionEndPriceOffsetFrom: params.auctionEndPriceOffsetFrom,
			slippageTolerance: derivedSlippage,
			isOracleOrder: params.isOracleOrder,
			additionalEndPriceBuffer: params.additionalEndPriceBuffer,
			forceUpToSlippage: params.forceUpToSlippage,
		});

		if (!auctionParams) {
			throw new Error(
				'Failed to derive auction params from blockchain fallback'
			);
		}

		// Convert the auction params to the expected format
		const mappedAuctionParams =
			convertOptionalOrderParamsToMappedAuctionParams(auctionParams);

		return {
			auctionParams: mappedAuctionParams,
			slippage: derivedSlippage,
			priceImpact: {
				entryPrice: priceImpactData.entryPrice,
				markPrice: markPrice,
				priceImpact: priceImpactData.priceImpact,
				baseAvailable: priceImpactData.baseAvailable,
				bestPrice: priceImpactData.bestPrice,
				worstPrice: priceImpactData.worstPrice,
				exceedsLiquidity: priceImpactData.exceedsLiquidity,
				showPriceEstimateOracleDivergenceWarning:
					calculateShowPriceEstimateOracleDivergenceWarning(
						priceImpactData.entryPrice,
						oraclePrice,
						params.direction === PositionDirection.LONG ? 'buy' : 'sell',
						'market', // Fallback is typically for market orders
						!params.baseAmount.isZero()
					),
			},
			oraclePrice,
			source: 'blockchain',
		};
	} finally {
		// Always clean up the OrderSubscriber
		try {
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		} catch (cleanupError) {
			// Log cleanup error but don't throw since we want to return the result
			console.error('Error cleaning up OrderSubscriber:', cleanupError);
		}
	}
};

const getOrderParamsWithFallback = async (params: {
	marketId: MarketId;
	direction: PositionDirection;
	baseAmount: BN;
	reduceOnly: boolean;
	auctionDuration: number;
	auctionStartPriceOffset: number;
	auctionEndPriceOffset: number;
	auctionStartPriceOffsetFrom: TradeOffsetPrice;
	auctionEndPriceOffsetFrom: TradeOffsetPrice;
	auctionPriceCaps?: { min: BN; max: BN };
	slippageTolerance: number | 'dynamic';
	isOracleOrder?: boolean;
	additionalEndPriceBuffer?: BN;
	forceUpToSlippage?: boolean;
	maxLeverageSelected: boolean;
	maxLeverageOrderSize: BN;
	velocityClient: VelocityClient;
}): Promise<ProcessedOrderParamsResponse> => {
	try {
		// First fallback: Try server-based L2 fetching
		const l2DataArray = await fetchL2DataForMarket(params.marketId);
		const oraclePriceData = l2DataArray[0].oracleData;
		const markPriceBn = l2DataArray[0].markPrice;
		const l2Data = convertToL2OrderBook(l2DataArray);

		// Get oracle and mark prices - we need these for the auction params calculation
		if (!params.velocityClient) {
			throw new Error('Server L2 fallback requires driftClient');
		}

		const oraclePriceBn = oraclePriceData?.price;

		if (!oraclePriceBn || !markPriceBn) {
			throw new Error('Missing oracle or mark price for server L2 fallback');
		}

		// Calculate price impact
		const priceImpactData = calculatePriceImpactFromL2(
			params.marketId,
			params.direction,
			params.baseAmount,
			l2Data,
			oraclePriceBn
		);

		const startPrices = getPriceObject({
			oraclePrice: oraclePriceBn,
			bestOffer: priceImpactData.bestPrice,
			entryPrice: priceImpactData.entryPrice,
			worstPrice: priceImpactData.worstPrice,
			markPrice: markPriceBn,
			direction: params.direction,
		});

		// Calculate slippage using L2 data
		const derivedSlippage =
			params.slippageTolerance === 'dynamic'
				? calculateDynamicSlippageFromL2(
						l2Data,
						params.marketId,
						startPrices[
							params.auctionStartPriceOffsetFrom as keyof typeof startPrices
						],
						priceImpactData.worstPrice,
						oraclePriceBn
					)
				: typeof params.slippageTolerance === 'number'
					? params.slippageTolerance
					: 0.005;

		// Use deriveMarketOrderParams to get auction params
		const auctionParams = deriveMarketOrderParams({
			marketType: params.marketId.marketType,
			marketIndex: params.marketId.marketIndex,
			direction: params.direction,
			baseAmount: params.baseAmount,
			reduceOnly: params.reduceOnly,
			allowInfSlippage: false,
			worstPrice: priceImpactData.worstPrice,
			auctionDuration: params.auctionDuration,
			auctionStartPriceOffset: params.auctionStartPriceOffset,
			auctionEndPriceOffset: params.auctionEndPriceOffset,
			maxLeverageSelected: params.maxLeverageSelected,
			maxLeverageOrderSize: params.maxLeverageOrderSize,
			oraclePrice: oraclePriceBn,
			markPrice: markPriceBn,
			bestPrice: priceImpactData.bestPrice,
			entryPrice: priceImpactData.entryPrice,
			auctionStartPriceOffsetFrom: params.auctionStartPriceOffsetFrom,
			auctionEndPriceOffsetFrom: params.auctionEndPriceOffsetFrom,
			slippageTolerance: derivedSlippage,
			isOracleOrder: params.isOracleOrder,
			additionalEndPriceBuffer: params.additionalEndPriceBuffer,
			forceUpToSlippage: params.forceUpToSlippage,
		});

		if (!auctionParams) {
			throw new Error(
				'Failed to derive auction params from server L2 fallback'
			);
		}

		// Convert the auction params to the expected format
		const mappedAuctionParams =
			convertOptionalOrderParamsToMappedAuctionParams(auctionParams);

		return {
			auctionParams: mappedAuctionParams,
			slippage: derivedSlippage,
			priceImpact: {
				entryPrice: priceImpactData.entryPrice,
				markPrice: markPriceBn,
				priceImpact: priceImpactData.priceImpact,
				baseAvailable: priceImpactData.baseAvailable,
				bestPrice: priceImpactData.bestPrice,
				worstPrice: priceImpactData.worstPrice,
				exceedsLiquidity: priceImpactData.exceedsLiquidity,
				showPriceEstimateOracleDivergenceWarning:
					calculateShowPriceEstimateOracleDivergenceWarning(
						priceImpactData.entryPrice,
						oraclePriceBn,
						params.direction === PositionDirection.LONG ? 'buy' : 'sell',
						'market', // Fallback is typically for market orders
						!params.baseAmount.isZero()
					),
			},
			oraclePrice: oraclePriceBn,
			source: 'server',
		};
	} catch (serverL2Error) {
		console.warn(
			'Server L2 fallback failed, trying blockchain fallback:',
			serverL2Error
		);

		// Second fallback: Try blockchain-based L2 fetching
		return await getOrderParamsWithBlockchainFallback(params);
	}
};

const processServerResponse = (
	body: OrderParamsResponseBody,
	originalSlippageTolerance: number | string,
	side?: 'buy' | 'sell',
	orderType?: 'market' | 'oracle',
	hasBaseSize?: boolean
): ProcessedOrderParamsResponse => {
	// Check if the response has the expected structure
	if (!body.data || !body.data.params) {
		throw new Error('Invalid response structure from server');
	}

	const responseData = body.data;
	const rawAuctionParams = responseData.params;

	// Map the auction params using the existing mapper
	const mappedAuctionParams = mapAuctionParamsResponse(rawAuctionParams);

	// Process slippage to always be a number
	// Server response should contain slippageTolerance, but fallback to original if not
	const serverCalculatedSlippage = responseData.slippageTolerance;
	const processedSlippage =
		serverCalculatedSlippage !== undefined
			? typeof serverCalculatedSlippage === 'string'
				? parseFloat(serverCalculatedSlippage)
				: serverCalculatedSlippage
			: typeof originalSlippageTolerance === 'string'
				? parseFloat(originalSlippageTolerance)
				: originalSlippageTolerance;

	const oraclePrice = responseData.oraclePrice
		? new BN(responseData.oraclePrice)
		: new BN(0);

	// Process price impact data if available
	let processedPriceImpact;
	if (
		responseData.entryPrice !== undefined ||
		responseData.priceImpact !== undefined
	) {
		const entryPrice = responseData.entryPrice
			? new BN(responseData.entryPrice)
			: new BN(0);

		if (entryPrice.eq(ZERO)) {
			throw new Error('Entry price is zero');
		}

		processedPriceImpact = {
			entryPrice,
			markPrice: responseData.markPrice
				? new BN(responseData.markPrice)
				: new BN(0),
			priceImpact: responseData.priceImpact
				? new BN(responseData.priceImpact)
				: new BN(0),
			baseAvailable: responseData.baseAvailable
				? new BN(responseData.baseAvailable)
				: new BN(0),
			bestPrice: responseData.bestPrice
				? new BN(responseData.bestPrice)
				: new BN(0),
			worstPrice: responseData.worstPrice
				? new BN(responseData.worstPrice)
				: new BN(0),
			exceedsLiquidity: responseData.exceedsLiquidity || false,
			showPriceEstimateOracleDivergenceWarning:
				oraclePrice && side && orderType && hasBaseSize
					? calculateShowPriceEstimateOracleDivergenceWarning(
							entryPrice,
							oraclePrice,
							side,
							orderType,
							hasBaseSize
						)
					: false,
		};
	} else {
		throw new Error('No price impact data available from server');
	}

	return {
		auctionParams: mappedAuctionParams,
		slippage: processedSlippage,
		priceImpact: processedPriceImpact,
		oraclePrice,
		source: 'endpoint',
	};
};

/**
 * @deprecated Spot-only legacy auction-params client, superseded by
 * `@velocity-exchange/common` `fetchAuctionOrderParams` per
 * docs/adr/0001-auction-params-single-client.md.
 */
export const getOrderParams = async ({
	assetType,
	marketType,
	marketIndex,
	direction,
	maxLeverageSelected,
	maxLeverageOrderSize,
	amount,
	reduceOnly,
	auctionDurationSeconds,
	slotDuration,
	auctionStartPriceOffset,
	auctionEndPriceOffset,
	auctionStartPriceOffsetFrom,
	auctionEndPriceOffsetFrom,
	auctionPriceCaps,
	slippageTolerance,
	isOracleOrder,
	additionalEndPriceBuffer,
	forceUpToSlippage,
	velocityClient,
	orderType,
	userOrderId,
}: {
	assetType: 'base' | 'quote';
	marketType: MarketType;
	marketIndex: number;
	direction: PositionDirection;
	maxLeverageSelected: boolean;
	maxLeverageOrderSize: BN;
	amount: BN;
	reduceOnly: boolean;
	/** The trader's wall-clock auction ramp; converted to slots below. */
	auctionDurationSeconds: number;
	slotDuration: SlotDurationMs;
	auctionStartPriceOffset: number;
	auctionEndPriceOffset: number;
	auctionStartPriceOffsetFrom: TradeOffsetPrice;
	auctionEndPriceOffsetFrom: TradeOffsetPrice;

	slippageTolerance: number | 'dynamic';
	velocityClient: VelocityClient;
	auctionPriceCaps?: {
		min: BN;
		max: BN;
	};
	isOracleOrder?: boolean;
	additionalEndPriceBuffer?: BN;
	forceUpToSlippage?: boolean;
	orderType?: 'market' | 'oracle';
	userOrderId?: number;
}): Promise<ProcessedOrderParamsResponse> => {
	const marketId = new MarketId(marketIndex, marketType);

	if (!amount || amount.isZero()) {
		throw new Error('Not fetching auction params for zero amount');
	}

	// Check if feature flag is enabled to force fallback
	const forceFallback = useFeatureFlagStore
		.getState()
		.flagEnabled('FORCE_ORDER_PARAMS_FALLBACK');

	const apiVersion =
		useFeatureFlagStore.getState().flags.AUCTION_PARAMS_VERSION.version;

	// If user is using the default auction duration, apply the version-specific default
	const isUsingDefaultAuctionDuration =
		auctionDurationSeconds === DEFAULT_MARKET_AUCTION_DURATION_SECONDS;
	const resolvedAuctionDurationSlots = isUsingDefaultAuctionDuration
		? auctionDurationMsToSlots(
				DEFAULT_AUCTION_DURATION_MS_BY_VERSION[apiVersion] ??
					DEFAULT_MARKET_AUCTION_DURATION_MS,
				slotDuration
			)
		: auctionDurationSecondsToSlots(auctionDurationSeconds, slotDuration);

	let serverError: any = null;

	// Try server request unless feature flag forces fallback
	if (!forceFallback) {
		try {
			const urlParams = new URLSearchParams({
				assetType: assetType,
				marketType: ENUM_UTILS.toStr(marketType),
				marketIndex: marketIndex.toString(),
				direction: ENUM_UTILS.toStr(direction),
				maxLeverageSelected: maxLeverageSelected.toString(),
				maxLeverageOrderSize: maxLeverageOrderSize.toString(),
				amount: amount.toString(),
				reduceOnly: reduceOnly.toString(),
				// Omit auctionDuration when using the default so the server can
				// derive it from `version` instead of the client forcing a value.
				...(!isUsingDefaultAuctionDuration && {
					auctionDuration: resolvedAuctionDurationSlots.toString(),
				}),
				auctionStartPriceOffset: auctionStartPriceOffset.toString(),
				auctionEndPriceOffset: auctionEndPriceOffset.toString(),
				auctionStartPriceOffsetFrom: auctionStartPriceOffsetFrom,
				auctionEndPriceOffsetFrom: auctionEndPriceOffsetFrom,
				slippageTolerance: slippageTolerance?.toString() ?? 'dynamic',
				version: apiVersion.toString(),
				...(userOrderId !== undefined && {
					userOrderId: userOrderId.toString(),
				}),
				...(isOracleOrder !== undefined && {
					isOracleOrder: isOracleOrder.toString(),
				}),
				...(additionalEndPriceBuffer && {
					additionalEndPriceBuffer: additionalEndPriceBuffer.toString(),
				}),
				...(forceUpToSlippage !== undefined && {
					forceUpToSlippage: forceUpToSlippage.toString(),
				}),
				...(auctionPriceCaps && {
					auctionPriceCapsMin: auctionPriceCaps.min.toString(),
					auctionPriceCapsMax: auctionPriceCaps.max.toString(),
				}),
			});

			const response = await fetch(
				`${Env.dlobServerHttpUrl}/auctionParams?${urlParams.toString()}`
			);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const body = await response.json();

			// Process and return the fully mapped response
			return processServerResponse(
				body,
				slippageTolerance,
				ENUM_UTILS.match(direction, PositionDirection.LONG) ? 'buy' : 'sell',
				orderType,
				!amount.isZero()
			);
		} catch (error: any) {
			serverError = error;
		}
	}

	// Either feature flag is enabled or server failed - use fallback
	const fallbackReason = forceFallback
		? 'FORCE_ORDER_PARAMS_FALLBACK feature flag enabled'
		: 'Server auction params failed';

	console.log(
		`${fallbackReason}, falling back to client-side calculation:`,
		serverError ?? ''
	);

	try {
		// Fallback to client-side calculation
		return await getOrderParamsWithFallback({
			marketId,
			direction,
			baseAmount: amount,
			reduceOnly,
			auctionDuration: resolvedAuctionDurationSlots,
			auctionStartPriceOffset,
			auctionEndPriceOffset,
			auctionStartPriceOffsetFrom,
			auctionEndPriceOffsetFrom,
			auctionPriceCaps,
			slippageTolerance: slippageTolerance as number | 'dynamic',
			isOracleOrder,
			additionalEndPriceBuffer,
			forceUpToSlippage,
			maxLeverageSelected,
			maxLeverageOrderSize,
			velocityClient,
		});
	} catch (fallbackError: any) {
		captureException(fallbackError);
		console.error(
			'Both server and fallback auction params failed:',
			fallbackError
		);
		throw new Error(
			'Failed to get auction params from both server and fallback',
			{ cause: fallbackError }
		);
	}
};
